import axios from 'axios';

class ApiMethods {
    constructor() {
        this.apiUser = process.env.REACT_APP_API_USER;

        const hostname = window.location.hostname;

        if (hostname === 'localhost') {
            // LOCAL
            this.apiUrl = process.env.REACT_APP_API_URL_LOCAL;
            this.apiKey = process.env.REACT_APP_API_KEY_LOCAL;
            this.destinationsApiUrl = process.env.REACT_APP_DESTINATIONS_API_URL_LOCAL;
            this.destinationsApiKey = process.env.REACT_APP_DESTINATIONS_API_KEY_LOCAL;
        } else if (hostname.includes('dev.')) {
            // DEV
            this.apiUrl = process.env.REACT_APP_API_URL_DEV;
            this.apiKey = process.env.REACT_APP_API_KEY_DEV;
            this.destinationsApiUrl = process.env.REACT_APP_DESTINATIONS_API_URL_DEV;
            this.destinationsApiKey = process.env.REACT_APP_DESTINATIONS_API_KEY_DEV;
        } else if (hostname.includes('qa.')) {
            // QA
            this.apiUrl = process.env.REACT_APP_API_URL_QA;
            this.apiKey = process.env.REACT_APP_API_KEY_QA;
            this.destinationsApiUrl = process.env.REACT_APP_DESTINATIONS_API_URL_QA;
            this.destinationsApiKey = process.env.REACT_APP_DESTINATIONS_API_KEY_QA;
        } else {
            //PROD
            this.apiUrl = process.env.REACT_APP_API_URL_PROD;
            this.apiKey = process.env.REACT_APP_API_KEY_PROD;
            this.destinationsApiUrl = process.env.REACT_APP_DESTINATIONS_API_URL_PROD;
            this.destinationsApiKey = process.env.REACT_APP_DESTINATIONS_API_KEY_PROD;
        }

        this.token = null;
        this.tokenExpiration = null;
    }

    async getUserIP() {
        try {
            const response = await axios.get('https://api.ipify.org?format=json');
            return response.data.ip;
        } catch (error) {
            console.error('Cannot get the user IP:', error);
            return "0.0.0.0";
        }
    }

    async login() {
        try {
            const response = await axios.post(`${this.apiUrl}/Login`, {
                ApiUser: this.apiUser,
                ApiKey: this.apiKey,
            }, {
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            this.token = response.data.token;
            this.tokenExpiration = Date.now() + (60 * 60 * 1000); // 60 minutes in milliseconds

            return response.data;
        } catch (error) {
            console.error('Error during login:', error);
            throw error;
        }
    }

    async getReview(appsGuid) {
        if (!this.token || this.tokenExpiration < Date.now()) {
            await this.login();
        }

        try {
            const url = `${this.apiUrl}/ConsumerComm/Review/${appsGuid}`;
            
            const response = await axios.get(url, {
                headers: {
                    Authorization: `Bearer ${this.token}`
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error during getReview:', error.response ? error.response.data : error.message);
            throw error;
        }
    }

    async getDestinations() {
        if (!this.token || this.tokenExpiration < Date.now()) {
            await this.login();
        }

        try {
            const params = {
                ApiKey: this.destinationsApiKey,
                AgencyID: 0,
                Locality: "", // Leave empty if not needed
                Brand: "travelleaders",
            };
            const queryString = new URLSearchParams(params).toString();
            const fullUrl = `${this.destinationsApiUrl}?${queryString}`;

            const response = await axios.get(fullUrl, {
                responseType: 'text', // Waiting for a text response (e.g., XML)
            });

            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async submitReview(reviewData) {
        if (!this.token || this.tokenExpiration < Date.now()) {
            await this.login();
        }

        try {
            const response = await axios.post(`${this.apiUrl}/ConsumerComm/Review/Save`, reviewData, {
                headers: {
                    Authorization: `Bearer ${this.token}`
                }
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async getSurvey(appsGuid, requestId) {
        if (!this.token || this.tokenExpiration < Date.now()) {
            await this.login();
        }

        try {
            const response = await axios.get(`${this.apiUrl}/ConsumerComm/Survey/${appsGuid}/${requestId}`, {
                headers: {
                    Authorization: `Bearer ${this.token}`
                }
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async getOldSurvey(requestId) {
        if (!this.token || this.tokenExpiration < Date.now()) {
            await this.login();
        }

        try {
            const response = await axios.get(`${this.apiUrl}/ConsumerComm/Survey/${requestId}/0`, {
                headers: {
                    Authorization: `Bearer ${this.token}`
                }
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async submitSurvey(surveyData) {
        if (!this.token || this.tokenExpiration < Date.now()) {
            await this.login();
        }
        try {
            const response = await axios.post(`${this.apiUrl}/ConsumerComm/Survey/Save`, surveyData, {
                headers: {
                    Authorization: `Bearer ${this.token}`
                }
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async getCustomUrl(customUrlData) {
        if (!this.token || this.tokenExpiration < Date.now()) {
            await this.login();
        }

        try {
            const response = await axios.get(`${this.apiUrl}/ConsumerComm/CustomURL/${customUrlData}`, {
                headers: {
                    Authorization: `Bearer ${this.token}`
                }
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async getContacted(appsGuid, requestId) {
        if (!this.token || this.tokenExpiration < Date.now()) {
            await this.login();
        }

        const userIP = await this.getUserIP();

        try {
            const response = await axios.get(`${this.apiUrl}/ConsumerComm/Contacted/${appsGuid}/${requestId}`, {
                headers: {
                    Authorization: `Bearer ${this.token}`,
                    Token: appsGuid,
                    RequestId: requestId,
                    UserIP: userIP,
                    RequestUrl: `${this.apiUrl}/ConsumerComm/Contacted/${appsGuid}/${requestId}`
                }
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async postContacted(appsGuid, requestId, userResponse, requestUrl) {
        if (!this.token || this.tokenExpiration < Date.now()) {
            await this.login();
        }

        const userIP = await this.getUserIP();
        try {
            const response = await axios.post(`${this.apiUrl}/ConsumerComm/Contacted/Save`, {
                appsGuid: appsGuid,
                RequestId: requestId,
                Contacted: userResponse,
                UserIP: userIP,
                RequestUrl: requestUrl,
            }, {
                headers: {
                    Authorization: `Bearer ${this.token}`
                }
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async logError(error, userDevice) {

        const loginErrorMessage = "";
        if (!this.token || this.tokenExpiration < Date.now()) {
            try {
                await this.login();
            } catch (loginError) {
                loginErrorMessage = ` | TOKEN ERROR: ${loginError.message || "Failed to refresh token"}`;
            }
        }

        const userIP = await this.getUserIP();

        const errorName = error.response?.data?.title?.trim()
            ? error.response.data.title
            : error.message || "Unknown error name";

        const finalMessage = `ERROR: ${errorName} ${loginErrorMessage} | IP: ${userIP || "Unknown IP"} | DEVICE: ${userDevice}`;

        const fileName = (new Error()).stack.split("\n")[1].match(/\/([^/]+)\.(js|jsx)/);
        const sourceSystem = fileName ? fileName[1] : "UnknownFile";

        try {
            const response = await axios.post(`${this.apiUrl}/Logs/Error`, {
                SourceSystem: sourceSystem,
                Message: finalMessage,
            }, {
                headers: {
                    Authorization: `Bearer ${this.token}`
                }
            });

            return response.data;
        } catch (logError) {
            console.error("Error logging failed:", logError);
            throw logError;
        }
    }

}

const apiMethodsInstance = new ApiMethods();

export default apiMethodsInstance;
